'use client';

import { memo, useState, type ReactNode } from 'react';

import {
  mediaUrl,
  type ImageVariantKey,
  type MediaRef,
} from '@kit/shared/storage';

import { cn } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../shadcn/avatar';

export interface MediaAvatarProps {
  /**
   * Typed reference to the media — `{ bucket, path, visibility }` (+ optional
   * `externalUrl` / `r2Key`), NOT a pre-resolved string. The component calls
   * `mediaUrl` internally, so a raw bucket-relative storage path can never
   * reach an `<img src>` and 404 — the recurring "raw path in AvatarImage"
   * bug (2026-07-05) becomes unrepresentable, because there is no `src`
   * string prop to misuse. Pass `null` when there is no media.
   *
   * @example
   * <MediaAvatar
   *   media={{ bucket: StorageBuckets.DANCER_MEDIA, path: profile.profile_photo_url, visibility: 'public' }}
   *   fallback={initials}
   * />
   */
  media?: MediaRef | null;
  /** Named transform variant. Defaults to THUMBNAIL (avatars are small). */
  variant?: ImageVariantKey;
  /** Fallback content (initials / icon) shown when there is no image, the
   * media resolves to null, or the image fails to load. */
  fallback: ReactNode;
  /** Alt text for the image. */
  alt?: string;
  className?: string;
  fallbackClassName?: string;
}

/**
 * Media-aware avatar: the ONE render boundary for storage-backed avatars.
 *
 * Callers hand over a typed `MediaRef` (bucket + path), and the component
 * resolves it to a fetchable URL via `mediaUrl` — the same token-free
 * `/api/media/cover` proxy / R2-transform / public-URL resolution every
 * loader used to re-implement (and occasionally forget). Consolidating that
 * resolution here means a new call site cannot silently reintroduce the
 * raw-path bug: there is no `src` prop, only a ref.
 *
 * Memoized for large lists (rosters, cast grids, search results).
 */
export const MediaAvatar = memo(function MediaAvatar({
  media,
  variant = 'THUMBNAIL',
  fallback,
  alt = '',
  className,
  fallbackClassName,
}: MediaAvatarProps) {
  const [errored, setErrored] = useState(false);
  const src = media ? mediaUrl(media, variant) : null;

  return (
    <Avatar className={className}>
      {!errored && src ? (
        <AvatarImage src={src} alt={alt} onError={() => setErrored(true)} />
      ) : null}
      <AvatarFallback className={cn(fallbackClassName)}>
        {fallback}
      </AvatarFallback>
    </Avatar>
  );
});
